import React, { useRef, useCallback, useEffect, useState } from 'react';
import { Box, CircularProgress, Typography, Fab } from '@mui/material';
import { KeyboardArrowDown } from '@mui/icons-material';
import { MessageListProps, ChatMessage } from '../../../types/chat';
import MessageItem from './MessageItem';

const MessageList: React.FC<MessageListProps> = ({
  messages,
  currentUserId,
  onLoadMore,
  onMessageLongPress,
  onImagePress,
  onReactionPress,
  onReplyPress,
  onDeletePress,
  loading,
  hasMore,
  currentUserType,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const loadMoreObserverRef = useRef<IntersectionObserver | null>(null);
  const loadMoreTriggerRef = useRef<HTMLDivElement>(null);
  const loadingRef = useRef(false);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isNearBottomRef = useRef(true); // Track near-bottom state without causing re-renders
  const hasScrolledToBottomRef = useRef(false); // Track if we've ever scrolled to bottom
  const previousScrollHeight = useRef(0); // Track scroll height for position preservation
  const previousMessageCount = useRef(0); // Track message count to detect new loads
  const isLoadingOlderMessages = useRef(false); // Track if we're currently loading older messages

  // State for showing scroll-to-bottom button
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const [isNearBottom, setIsNearBottom] = useState(true);
  const [showScrollToTop, setShowScrollToTop] = useState(false);

  // Track ongoing scroll animation to prevent conflicts
  const scrollAnimationRef = useRef<number | null>(null);

  // Custom smooth scroll function for better control
  const smoothScrollToPosition = useCallback(
    (container: HTMLElement, targetPosition: number, duration = 800) => {
      // Cancel any existing animation
      if (scrollAnimationRef.current) {
        cancelAnimationFrame(scrollAnimationRef.current);
      }

      const startPosition = container.scrollTop;
      const distance = targetPosition - startPosition;
      const startTime = performance.now();

      // Easing function for smooth animation (ease-out-quart)
      const easeOutQuart = (t: number) => 1 - Math.pow(1 - t, 4);

      const animateScroll = (currentTime: number) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const easeProgress = easeOutQuart(progress);

        container.scrollTop = startPosition + distance * easeProgress;

        if (progress < 1) {
          scrollAnimationRef.current = requestAnimationFrame(animateScroll);
        } else {
          scrollAnimationRef.current = null;
        }
      };

      scrollAnimationRef.current = requestAnimationFrame(animateScroll);
    },
    [],
  );

  // Same 5-minute grouping logic as mobile
  const shouldGroupWithPrevious = useCallback(
    (current: ChatMessage, previous?: ChatMessage) => {
      if (!previous) return false;

      const timeDiff = Math.abs(
        new Date(current.createdOn).getTime() -
          new Date(previous.createdOn).getTime(),
      );

      return current.fromUID === previous.fromUID && timeDiff < 300000; // 5 minutes
    },
    [],
  );

  // Preserve scroll position when older messages are loaded
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // If message count increased (new messages loaded at top)
    if (messages.length > previousMessageCount.current) {
      const currentScrollHeight = container.scrollHeight;
      const scrollHeightDiff =
        currentScrollHeight - previousScrollHeight.current;

      // Only adjust if messages were added at the top (scroll height increased significantly)
      if (scrollHeightDiff > 50) {
        console.log(
          "📜 Preserving scroll position after loading older messages",
          {
            previousCount: previousMessageCount.current,
            currentCount: messages.length,
            scrollHeightDiff,
            currentScrollTop: container.scrollTop,
          },
        );

        // Use requestAnimationFrame to ensure DOM has updated
        requestAnimationFrame(() => {
          if (containerRef.current) {
            containerRef.current.scrollTop =
              containerRef.current.scrollTop + scrollHeightDiff;
          }
        });
      }
    }

    // Update refs for next comparison
    previousMessageCount.current = messages.length;
    previousScrollHeight.current = container.scrollHeight;
  }, [messages.length]);

  // Simple auto-scroll: scroll to bottom when messages first load or when current user sends
  useEffect(() => {
    if (messages.length > 0 && messagesEndRef.current) {
      const lastMessage = messages[messages.length - 1];
      const isCurrentUserMessage = lastMessage?.fromUID === currentUserId;
      const isInitialLoad = !hasScrolledToBottomRef.current;

      // Don't auto-scroll if we're loading older messages
      if (isLoadingOlderMessages.current) {
        console.log("🚫 Auto-scroll skipped: loading older messages");
        return;
      }

      // Scroll if: 1) First time loading messages, 2) Current user sent message, 3) User is at bottom
      const shouldScroll =
        isInitialLoad || isCurrentUserMessage || isNearBottomRef.current;

      if (shouldScroll) {
        console.log("📨 Scrolling to bottom:", {
          isInitialLoad,
          isCurrentUserMessage,
          isNearBottom: isNearBottomRef.current,
          messageCount: messages.length,
        });

        // Use a delay to ensure DOM is rendered, longer for initial load
        setTimeout(
          () => {
            if (messagesEndRef.current && containerRef.current) {
              // Use manual scroll positioning instead of scrollIntoView to prevent parent scroll
              const container = containerRef.current;
              const scrollToPosition =
                container.scrollHeight - container.clientHeight;

              if (isInitialLoad) {
                // Use gentle smooth scroll for initial load with longer duration
                smoothScrollToPosition(container, scrollToPosition, 1200);
              } else {
                // Use custom smooth scroll with gentler animation (600ms duration)
                smoothScrollToPosition(container, scrollToPosition, 600);
              }

              hasScrolledToBottomRef.current = true;
              setIsNearBottom(true);
              isNearBottomRef.current = true;
              setShowScrollToBottom(false);
            }
          },
          isInitialLoad ? 300 : 50, // Longer delay for initial load to let all messages render
        );
      }
    }
  }, [messages, currentUserId]);

  // Handle scroll position for scroll-to-bottom button - throttled to prevent stuttering
  const handleScroll = useCallback(() => {
    if (!containerRef.current) return; // Don't interfere with null container

    // Throttle scroll events to prevent stuttering
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    scrollTimeoutRef.current = setTimeout(() => {
      if (!containerRef.current) return;

      const { scrollTop, scrollHeight, clientHeight } = containerRef.current;

      // Check if user is near bottom (within 100px for better responsiveness)
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
      const nearBottom = distanceFromBottom < 100;

      // Check if user is near top (within 100px)
      const nearTop = scrollTop < 100;

      // Update both state and ref immediately for better responsiveness
      const previousNearBottom = isNearBottomRef.current;
      setIsNearBottom(nearBottom);
      isNearBottomRef.current = nearBottom;

      // Only show scroll-to-bottom if not near bottom and has messages
      setShowScrollToBottom(!nearBottom && messages.length > 0);
      setShowScrollToTop(!nearTop && scrollTop > 200);

      // Log significant scroll position changes
      if (previousNearBottom !== nearBottom) {
        console.log("📍 Scroll position changed:", {
          nearBottom,
          distanceFromBottom: Math.round(distanceFromBottom),
          scrollTop: Math.round(scrollTop),
        });
      }
    }, 50); // Reduced throttle for better responsiveness
  }, [messages.length]);

  // Scroll to bottom manually
  const scrollToBottom = useCallback(() => {
    if (containerRef.current && messages.length > 0) {
      console.log("⬇️ Manual scroll to bottom triggered");

      // Use container scroll instead of scrollIntoView
      const container = containerRef.current;
      const scrollToPosition = container.scrollHeight - container.clientHeight;

      // Use custom smooth scroll with moderate duration (700ms)
      smoothScrollToPosition(container, scrollToPosition, 700);

      // Update states immediately for better UX
      setShowScrollToBottom(false);
      setIsNearBottom(true);
      isNearBottomRef.current = true;
    }
  }, [messages.length, smoothScrollToPosition]);

  // Scroll to top manually
  const scrollToTop = useCallback(() => {
    if (containerRef.current) {
      console.log("🔝 Scrolling to top manually");

      // Use custom smooth scroll with moderate duration (800ms)
      smoothScrollToPosition(containerRef.current, 0, 800);
      setShowScrollToTop(false);
    }
  }, [smoothScrollToPosition]);

  // Debug scroll state
  useEffect(() => {
    if (containerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
      console.log("📏 Scroll Debug:", {
        scrollTop,
        scrollHeight,
        clientHeight,
        canScroll: scrollHeight > clientHeight,
        isNearBottom,
        messagesLength: messages.length,
      });
    }
  }, [messages.length, isNearBottom]);

  // Handle manual/automatic load more - defined early to be stable for intersection observer
  const handleLoadMore = useCallback(async () => {
    if (hasMore && !loading) {
      try {
        console.log("🔄 MessageList: Starting loadMore operation");
        isLoadingOlderMessages.current = true; // Flag that we're loading older messages
        await onLoadMore();
      } catch (error) {
        console.error("❌ MessageList: Failed to load more messages:", error);
      } finally {
        // Reset the flag after a short delay to let DOM updates complete
        setTimeout(() => {
          isLoadingOlderMessages.current = false;
        }, 100);
      }
    } else {
      console.log("🚫 MessageList: handleLoadMore skipped", {
        hasMore,
        loading,
        loadingRef: loadingRef.current,
      });
    }
  }, [hasMore, loading, onLoadMore]);

  // Cleanup function to cancel ongoing animations on unmount
  useEffect(() => {
    return () => {
      if (scrollAnimationRef.current) {
        cancelAnimationFrame(scrollAnimationRef.current);
      }
    };
  }, []);

  // Set up intersection observer for infinite scroll
  useEffect(() => {
    if (!loadMoreTriggerRef.current || !hasMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (
            entry.isIntersecting &&
            hasMore &&
            !loading &&
            !loadingRef.current
          ) {
            console.log(
              "🔄 Intersection Observer: Load more trigger activated",
              {
                isIntersecting: entry.isIntersecting,
                intersectionRatio: entry.intersectionRatio,
                hasMore,
                loading,
                loadingRef: loadingRef.current,
              },
            );
            handleLoadMore(); // loadingRef will be set inside handleLoadMore
          } else {
            console.log("🔄 Intersection Observer: Conditions not met", {
              isIntersecting: entry.isIntersecting,
              hasMore,
              loading,
              loadingRef: loadingRef.current,
            });
          }
        });
      },
      {
        root: containerRef.current, // Use the scroll container as root
        rootMargin: "50px 0px", // Reduced margin for more predictable triggering
        threshold: 0.1, // Lower threshold for more sensitive triggering
      },
    );

    observer.observe(loadMoreTriggerRef.current);
    loadMoreObserverRef.current = observer;

    return () => {
      if (loadMoreObserverRef.current) {
        loadMoreObserverRef.current.disconnect();
      }
    };
  }, [hasMore, loading, messages.length, handleLoadMore]); // Now handleLoadMore is properly in dependencies

  return (
    <Box
      sx={{
        flex: 1,
        overflow: "hidden",
        position: "relative",
        backgroundColor: "#f8f9fa",
      }}
      data-testid="messages-list"
    >
      {messages.length === 0 && loading ? (
        // Initial loading state
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <CircularProgress />
          <Typography variant="body2" color="text.secondary">
            Loading messages...
          </Typography>
        </Box>
      ) : messages.length === 0 ? (
        // Empty state
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <Typography variant="h6" color="text.secondary">
            No messages yet
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Start the conversation by sending a message below
          </Typography>
        </Box>
      ) : (
        // Simple scrollable message list
        <Box
          sx={{
            position: "relative",
            flex: 1,
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            ref={containerRef}
            onScroll={handleScroll}
            sx={{
              flex: 1,
              overflowY: "scroll", // Force scroll capability
              overflowX: "hidden",
              overflowAnchor: "none", // Prevent scroll anchoring affecting parent
              isolation: "isolate", // Create new stacking context
              contain: "layout style size", // CSS containment to prevent parent effects
              scrollbarWidth: "thin",
              WebkitOverflowScrolling: "touch", // Smooth scrolling on mobile
              position: "relative", // Ensure proper positioning context
              "&::-webkit-scrollbar": {
                width: "6px",
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: "transparent",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "rgba(0,0,0,0.2)",
                borderRadius: "3px",
                "&:hover": {
                  backgroundColor: "rgba(0,0,0,0.3)",
                },
              },
            }}
          >
            {/* Intersection Observer Trigger for Infinite Scroll */}
            {hasMore && (
              <Box
                ref={loadMoreTriggerRef}
                sx={{
                  height: 40, // Made taller for better intersection detection
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  backgroundColor: "rgba(248,249,250,0.95)",
                  mb: 1,
                  mx: 1,
                  borderRadius: 1,
                  border: "1px solid rgba(0,0,0,0.1)",
                  cursor: loading ? "default" : "pointer",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: loading
                      ? "rgba(248,249,250,0.95)"
                      : "rgba(248,249,250,1)",
                    transform: loading ? "none" : "translateY(-1px)",
                  },
                }}
                onClick={
                  !loading
                    ? () => {
                        console.log("👆 Load more trigger clicked manually", {
                          hasMore,
                          loading,
                          loadingRef: loadingRef.current,
                        });
                        handleLoadMore();
                      }
                    : undefined
                }
              >
                {loading ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CircularProgress size={16} />
                    <Typography variant="caption" color="text.secondary">
                      Loading older messages...
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography
                      variant="caption"
                      color="primary"
                      sx={{
                        fontSize: "12px",
                        fontWeight: 500,
                      }}
                    >
                      ↑ Click or scroll to load older messages
                    </Typography>
                  </Box>
                )}
              </Box>
            )}

            {/* Message list */}
            <Box sx={{ px: 2, pb: 2 }}>
              {messages.map((message, index) => {
                const previousMessage =
                  index > 0 ? messages[index - 1] : undefined;
                const nextMessage =
                  index < messages.length - 1 ? messages[index + 1] : undefined;
                const isCurrentUser = message.fromUID === currentUserId;
                const showUserInfo = !shouldGroupWithPrevious(
                  message,
                  previousMessage,
                );

                return (
                  <Box
                    key={message.id}
                    sx={{
                      mb: showUserInfo ? 2 : 0.5, // Proper spacing between message groups
                    }}
                  >
                    <MessageItem
                      message={message}
                      isCurrentUser={isCurrentUser}
                      previousMessage={previousMessage}
                      nextMessage={nextMessage}
                      showUserInfo={showUserInfo}
                      onLongPress={onMessageLongPress}
                      onImagePress={onImagePress}
                      onReactionPress={onReactionPress}
                      onReplyPress={onReplyPress}
                      onDeletePress={onDeletePress}
                      currentUserId={currentUserId}
                      currentUserType={currentUserType}
                    />
                  </Box>
                );
              })}
            </Box>

            {/* Scroll anchor for auto-scroll */}
            <div ref={messagesEndRef} />
          </Box>

          {/* Scroll to Bottom Button */}
          {showScrollToBottom && (
            <Fab
              size="small"
              color="primary"
              onClick={scrollToBottom}
              sx={{
                position: "absolute",
                bottom: 20,
                right: 20,
                zIndex: 2,
                opacity: 0.8,
                "&:hover": {
                  opacity: 1,
                },
              }}
            >
              <KeyboardArrowDown />
            </Fab>
          )}

          {/* Scroll to Top Button */}
          {showScrollToTop && (
            <Fab
              size="small"
              color="secondary"
              onClick={scrollToTop}
              sx={{
                position: "absolute",
                top: 20,
                right: 20,
                zIndex: 2,
                opacity: 0.8,
                "&:hover": {
                  opacity: 1,
                },
              }}
            >
              <KeyboardArrowDown sx={{ transform: "rotate(180deg)" }} />
            </Fab>
          )}
        </Box>
      )}
    </Box>
  );
};

export default React.memo(MessageList);
